import { CSSResultGroup } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import componentStyles from './se-list.css.lit';
import { OutlineList } from '../../base/outline-list/outline-list';

export const seListAlignments = [
  'left',
  'right',
  'center',
  'top',
  'bottom',
] as const;

export type SeListAlignment = typeof seListAlignments[number];

export const seListFontSizes = ['xs', 'sm', 'base', 'lg', 'xl', '2xl'] as const;

export type SeListFontSize = typeof seListFontSizes[number];

/**
 * The SeList component
 * @element se-list
 * @slot default slot.
 * @slot heading: for content above the default slot.
 * @slot footer: for content below the default slot.
 */
@customElement('se-list')
export class SeList extends OutlineList {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Passes css var size and line-height styles to slotted <li>s.
   */
  @property({ type: String, attribute: 'font-size' })
  fontSize: SeListFontSize;

  /**
   * A string formatted in standard css padding shorthand top|right|bottom|left
   */
  @property({ type: String })
  padding: string;

  /**
   * If set, sets all slotted <li>s to flex: 1 and if 'left/right/top/bottom' removes padding on specified side.
   */
  @property({ type: String })
  alignment: string;

  /**
   * Ref to the default slot.
   */
  @queryAssignedElements()
  items: HTMLElement[];

  setLiStyles() {
    if (this.fontSize) {
      this.items
        .filter(item => item.localName === 'li')
        .map(li => (li.style.fontSize = `var(--fs-${this.fontSize})`));
      this.items
        .filter(item => item.localName === 'li')
        .map(li => (li.style.lineHeight = `var(--lh-${this.fontSize})`));
    }
    if (this.padding) {
      this.items
        .filter(item => item.localName === 'li')
        .map(li => (li.style.padding = this.padding));
    }
    if (this.alignment) {
      this.items
        .filter(item => item.localName === 'li')
        .map(li => (li.style.flex = '1'));
      if (this.alignment !== 'center' && this.alignment !== 'undefined') {
        const paddingVar = `padding${
          this.alignment.charAt(0).toUpperCase() + this.alignment.slice(1)
        }`;
        this.items
          .filter(item => item.localName === 'li')
          //@ts-expect-error because typescript
          .map(li => (li.style[`${paddingVar}`] = '0'));
      }
    }
  }

  firstUpdated() {
    this.setLiStyles();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-list': SeList;
  }
}
